import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { getUserSettings, updateUserSettings } from '../lib/api';
import type { UserSettings } from '../types';

export default function SettingsPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [settings, setSettings] = useState<UserSettings>({
        city: '',
        state: '',
        country: 'USA'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/auth', { state: { returnTo: '/settings' } });
        }
    }, [user, authLoading, navigate]);

    // Load settings on mount
    useEffect(() => {
        async function loadSettings() {
            if (!user) return;

            try {
                const data = await getUserSettings();
                if (data) {
                    setSettings({
                        city: data.city || '',
                        state: data.state || '',
                        country: data.country || 'USA'
                    });
                }
            } catch (err) {
                console.error('Failed to load settings:', err);
            } finally {
                setLoading(false);
            }
        }

        if (user) {
            loadSettings();
        }
    }, [user]);

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSaving(true);
        setSaved(false);

        try {
            await updateUserSettings(settings);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    }

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-primary-50">
                <Header />
                <main className="max-w-lg mx-auto px-4 py-6">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                        <div className="space-y-4">
                            <div className="h-12 bg-gray-200 rounded"></div>
                            <div className="h-12 bg-gray-200 rounded"></div>
                            <div className="h-12 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary-50">
            <Header />

            <main className="max-w-lg mx-auto px-4 py-6 safe-bottom">
                {/* Back button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <span className="mr-2">←</span>
                    Back
                </button>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">Settings</h1>
                <p className="text-gray-500 mb-6">
                    Set your location for personalized recycling rules
                </p>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* City */}
                    <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="city"
                            type="text"
                            value={settings.city}
                            onChange={(e) => setSettings(s => ({ ...s, city: e.target.value }))}
                            required
                            placeholder="e.g. Austin"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                    </div>

                    {/* State/Province */}
                    <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                            State / Province
                        </label>
                        <input
                            id="state"
                            type="text"
                            value={settings.state}
                            onChange={(e) => setSettings(s => ({ ...s, state: e.target.value }))}
                            placeholder="e.g. Texas or TX"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                    </div>

                    {/* Country */}
                    <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                            Country
                        </label>
                        <select
                            id="country"
                            value={settings.country}
                            onChange={(e) => setSettings(s => ({ ...s, country: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white"
                        >
                            <option value="USA">United States</option>
                            <option value="Canada">Canada</option>
                            <option value="UK">United Kingdom</option>
                            <option value="Australia">Australia</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    {/* Info Box */}
                    <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <span className="text-2xl">🌍</span>
                            <div>
                                <p className="text-sm text-gray-700">
                                    <strong>Why we need your city:</strong> Recycling rules vary by location.
                                    We'll fetch your city's specific guidelines to give you accurate disposal instructions.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Success */}
                    {saved && (
                        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm flex items-center gap-2">
                            <span>✓</span> Settings saved successfully!
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={saving || !settings.city}
                        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </form>

                {/* Municipality ID (for debugging) */}
                {settings.municipalityId && (
                    <p className="text-xs text-gray-400 mt-4 text-center">
                        Municipality ID: {settings.municipalityId}
                    </p>
                )}
            </main>
        </div>
    );
}
